import axios from "axios";
import creatorBriefsApi from "../../stores/api/creatorBriefsApi";
import personasApi from "../../stores/api/personasApi";
import onboardingApi from "../../stores/api/onboardingApi";
import discoverCommunitiesApi from "../../stores/api/discoverCommunitiesApi";
import { OnboardingStatusResponse } from "../../stores/types/onboardingTypes";
import { CommunityAggregateDataType } from "../../stores/types/discoverCommunitiesTypes";

interface PollOptions {
  timeout?: number;
  interval?: number;
  signal?: AbortSignal;
}

export const pollForBriefGeneration = (
  briefId: number,
  options: PollOptions = {},
): Promise<void> => {
  const { timeout = 300000, interval = 5000, signal } = options;
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    const poll = setInterval(async () => {
      if (signal?.aborted) {
        clearInterval(poll);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(poll);
        console.warn(`Polling for brief ${briefId} timed out.`);
        reject(new Error("Polling timed out"));
        return;
      }

      try {
        const response = await creatorBriefsApi.fetchCreatorBriefById(briefId, {
          signal,
        });
        if (response.data.status !== "processing") {
          clearInterval(poll);
          resolve();
        }
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "AbortError") {
          clearInterval(poll);
          reject(new DOMException("Aborted", "AbortError"));
        } else {
          console.error(
            `Polling check for brief ${briefId} failed, retrying...`,
            error,
          );
        }
      }
    }, interval);

    signal?.addEventListener("abort", () => {
      clearInterval(poll);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
};

export const pollForPersonaGeneration = (
  options: PollOptions = {},
): Promise<void> => {
  const { timeout = 150000, interval = 5000, signal } = options;
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    const poll = setInterval(async () => {
      if (signal?.aborted) {
        clearInterval(poll);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(poll);
        reject("AI Persona generation timed out.");
        return;
      }

      try {
        const response = await personasApi.fetchPersonasData();
        const inProgressCount = response.data.filter(
          (p: { status: string }) => p.status === "In progress",
        ).length;

        if (inProgressCount === 0) {
          clearInterval(poll);
          resolve();
        }
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "AbortError") {
          clearInterval(poll);
          reject(new DOMException("Aborted", "AbortError"));
        } else {
          console.error("Polling check failed, retrying...", error);
        }
      }
    }, interval);

    signal?.addEventListener("abort", () => {
      clearInterval(poll);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
};

export const pollForPersonaCountChange = (
  initialCount: number,
  expectedIncrease: number,
  options: PollOptions = {},
): Promise<void> => {
  const { timeout = 150000, interval = 5000, signal } = options;
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    const poll = setInterval(async () => {
      if (signal?.aborted) {
        clearInterval(poll);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(poll);
        reject(new Error("AI Persona generation timed out."));
        return;
      }

      try {
        const response = await personasApi.fetchPersonasData();
        if (response.data.length >= initialCount + expectedIncrease) {
          clearInterval(poll);
          resolve();
        }
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "AbortError") {
          clearInterval(poll);
        } else {
          clearInterval(poll);
          reject(error);
        }
      }
    }, interval);

    signal?.addEventListener("abort", () => {
      clearInterval(poll);
    });
  });
};

export const pollForOnboardingSuccess = (
  taskId: string,
  options: PollOptions = {},
): Promise<OnboardingStatusResponse> => {
  const { timeout = 150000, interval = 5000, signal } = options;
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    const checkStatus = async () => {
      if (signal?.aborted) return;

      if (Date.now() - startTime >= timeout) {
        clearInterval(poll);
        return reject(new Error("Onboarding status check timed out."));
      }

      try {
        const response = await onboardingApi.fetchOnboardingStatus(taskId);

        if (response.data.status === "Success") {
          clearInterval(poll);
          resolve(response.data);
        } else if (response.data.status === "Failure") {
          clearInterval(poll);
          reject(
            new Error(response.data.error || "Onboarding process failed."),
          );
        }
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "AbortError") {
          clearInterval(poll);
          reject(new DOMException("Aborted", "AbortError"));
        } else {
          console.error("Error fetching onboarding status:", error);
          clearInterval(poll);
          reject(error);
        }
      }
    };

    const poll = setInterval(checkStatus, interval);
    checkStatus();

    signal?.addEventListener("abort", () => {
      clearInterval(poll);
    });
  });
};

export const pollForCreatorAggregate = (
  personaId: string | number,
  options: PollOptions = {},
): Promise<CommunityAggregateDataType> => {
  const { timeout = 600000, interval = 5000, signal } = options;
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    const checkCreatorData = async () => {
      if (signal?.aborted) {
        clearInterval(poll);
        return reject(new DOMException("Aborted", "AbortError"));
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(poll);
        return reject(
          new Error(`Polling for creators for persona ${personaId} timed out.`),
        );
      }

      try {
        const response =
          await discoverCommunitiesApi.fetchPersonaCommunitiesAggregateData({
            id: String(personaId),
          });

        if (
          response.data?.top_creators &&
          response.data.top_creators.length > 0
        ) {
          clearInterval(poll);
          resolve(response.data);
        }
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "AbortError") {
          clearInterval(poll);
          reject(new DOMException("Aborted", "AbortError"));
        } else {
          console.error(
            `Error fetching creator aggregate for persona ${personaId}:`,
            error,
          );
          clearInterval(poll);
          reject(error);
        }
      }
    };

    const poll = setInterval(checkCreatorData, interval);
    checkCreatorData();

    signal?.addEventListener("abort", () => {
      clearInterval(poll);
    });
  });
};
